import { z } from 'zod';
import { LIST_PAGE_SIZE } from '../../config.ts';
import { ORDER_STATUSES } from '../../ontology/objects/order.ts';
import { POSITION_STATUSES } from '../../ontology/objects/position.ts';
import { ORDER_SIDES } from '../../ontology/order.ts';
import { idSchema, TRADE_TYPES } from '../../ontology/primitives.ts';
import { day, instant, monthMoveSchema } from './primitives.ts';
import { wireLegSchema } from './recommendation.ts';

export const EXITED_POSITIONS_CAP = LIST_PAGE_SIZE;

export const wirePositionSchema = z.object({
  id: idSchema,
  recommendationId: idSchema,
  // Carried on the row: the recommendation may have fallen off the capped closed list.
  analystId: idSchema,
  bookId: idSchema,
  book: z.string(),
  tradeType: z.enum(TRADE_TYPES),
  status: z.enum(POSITION_STATUSES),
  underlying: z.string(),
  instrumentToken: z.number().int(),
  tradingsymbol: z.string(),
  // Not rendered, but order size units depend on the exchange.
  exchange: z.string(),
  expiry: day.nullable(),
  lotSize: z.number().int(),
  tickSize: z.number(),
  entryPrice: z.number(),
  quantity: z.number().int(),
  initialRisk: z.number().nullable(),
  ownStop: z.number().nullable(),
  ownTarget: z.number().nullable(),
  trails: z.boolean(),
  exitsWithRecommendation: z.boolean(),
  /** A resting limit is not a holding and must show no P/L (`isHeld`). Null is a manual trade. */
  entry: z.object({ status: z.enum(ORDER_STATUSES) }).nullable(),
  exits: z.enum(['desk', 'fallback', 'none']),
  fallbackStopPrice: z.number().nullable(),
  exitPrice: z.number().nullable(),
  exitedAt: instant.nullable(),
  // Not rendered, but read by `mergeFillGroup`.
  createdAt: instant,
});

export type WirePosition = z.infer<typeof wirePositionSchema>;

const ticketEntrySchema = z.discriminatedUnion('at', [
  z.object({ at: z.literal('analyst') }),
  z.object({ at: z.literal('market') }),
  z.object({ at: z.literal('custom'), price: z.number().positive() }),
]);

export type TicketEntry = z.infer<typeof ticketEntrySchema>;

const ticketSizeSchema = z.discriminatedUnion('by', [
  // Without an amount, the member's risk per trade in the book.
  z.object({ by: z.literal('risk'), amount: z.number().positive().optional() }),
  z.object({ by: z.literal('lots'), lots: z.number().int().positive() }),
  z.object({ by: z.literal('quantity'), quantity: z.number().int().positive() }),
]);

export type TicketSize = z.infer<typeof ticketSizeSchema>;

/** Asks only: the bot prices and sizes. Browser-computed prices and quantities are never trusted. */
export const ticketRefSchema = z.object({
  token: z.number().int().positive(),
  entry: ticketEntrySchema,
  size: ticketSizeSchema,
});

export const ticketRequestSchema = z.object({
  recommendationId: idSchema,
  positionId: idSchema.nullable().default(null),
  token: z.number().int().nullable().default(null),
  entry: ticketEntrySchema.nullable().default(null),
  size: ticketSizeSchema.nullable().default(null),
});

export type TicketRequest = z.infer<typeof ticketRequestSchema>;

const ticketPriceSchema = z.object({
  kind: z.enum(['market', 'analyst', 'day', 'budget', 'against']),
  price: z.number(),
  rr: z.number().nullable(),
});

export type TicketPrice = z.infer<typeof ticketPriceSchema>;

/** Figures are `tradeMetrics`' own; the browser must not recompute them. */
export const wireTicketSchema = z.object({
  legs: z.array(wireLegSchema.extend({ riskPerLot: z.number().nullable() })),
  token: z.number().int(),
  entry: ticketEntrySchema,
  size: ticketSizeSchema,
  ltp: z.number().nullable(),
  entryPrice: z.number().nullable(),
  quantity: z.number().int(),
  deployed: z.number(),
  analystAvailable: z.boolean(),
  /** The fallback stop Kite would refuse (inside its 0.25% band); the browser refuses on this, not its own check. */
  unparkable: z.object({ stop: z.number(), away: z.number(), needed: z.number() }).nullable(),
  /** The entry read on the tracked contract: from another leg, across `levels.shift`. */
  judged: z.number().nullable(),
  /** On another leg, `onLeg` is the analyst's levels it is sized to, `atGap` where they fire today. */
  levels: z.object({
    trackedPrice: z.number().nullable(),
    gap: z.number().nullable(),
    fallback: z.number().nullable(),
    shift: z.number().nullable(),
    onLeg: z.object({ stop: z.number(), target: z.number() }).nullable(),
    atGap: z.object({ stop: z.number(), target: z.number() }).nullable(),
  }),
  /** The reader's stop the price is already through, on the tracked contract; the browser refuses on this. */
  stopThrough: z.number().nullable(),
  /** The underlying, where NSE's F&O ban refuses a fresh entry on this leg today; the browser refuses on this. */
  banned: z.string().nullable(),
  /** A key event landing on this name while its venue is shut, said as the brief says it. */
  heldInto: z.string().nullable(),
  riskBudget: z.number(),
  asksExit: z.boolean(),
  riskPerUnit: z.number().nullable(),
  riskPerLot: z.number().nullable(),
  riskSized: z.number().int(),
  riskSteps: z.array(z.object({ amount: z.number(), quantity: z.number().int() })),
  metrics: z
    .object({
      rewardPerUnit: z.number(),
      risk: z.number(),
      reward: z.number(),
      riskPct: z.number(),
      rewardPct: z.number(),
      rr: z.number().nullable(),
    })
    .nullable(),
  day: z.object({ high: z.number(), low: z.number() }).nullable(),
  band: z.object({ lower: z.number(), upper: z.number() }).nullable(),
  prices: z.array(ticketPriceSchema),
});

export type WireTicket = z.infer<typeof wireTicketSchema>;

const rollLegSchema = z.object({
  side: z.enum(ORDER_SIDES),
  price: z.number().nullable(),
  levels: z.number().int(),
  thin: z.boolean(),
});

export type RollLeg = z.infer<typeof rollLegSchema>;

export const rolloverPreviewSchema = monthMoveSchema.extend({
  positionId: idSchema,
  out: rollLegSchema.nullable(),
  into: rollLegSchema.nullable(),
  charges: z.number().nullable(),
});

export type RolloverPreview = z.infer<typeof rolloverPreviewSchema>;

export const exitPreviewSchema = z.object({
  out: rollLegSchema,
  last: z.object({ price: z.number(), at: instant }).nullable(),
  day: z.object({ high: z.number(), low: z.number() }).nullable(),
  charges: z.number().nullable(),
  /** Why no order can go now, as the exit's refusal would say it. */
  shut: z.string().nullable(),
});

export type ExitPreview = z.infer<typeof exitPreviewSchema>;

export const sessionsReplySchema = z.object({
  /** Oldest first; none where Kite has not said them. */
  sessions: z.array(z.object({ day, high: z.number(), low: z.number() })),
});

export type SessionsReply = z.infer<typeof sessionsReplySchema>;
